import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import logger from '../utils/logger';

export class RelationshipService {
  // Maps relationships to their reciprocal types
  getReciprocalRelationship(relationshipType) {
    const relationshipMap = {
      'parent': 'child',
      'child': 'parent',
      'spouse': 'spouse',
      'sibling': 'sibling',
      'grandparent': 'grandchild',
      'grandchild': 'grandparent',
      'aunt/uncle': 'niece/nephew',
      'niece/nephew': 'aunt/uncle',
      'cousin': 'cousin'
    };
    return relationshipMap[relationshipType] || null;
  }

  async getProfile(userId) {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return null;
    }
    return {
      id: userDoc.id,
      ...userDoc.data()
    };
  }

  async addRelationship(userId, relatedUserId, relationshipType) {
    try {
      // Validate users exist and are in the same family
      const [userProfile, relatedProfile] = await Promise.all([
        this.getProfile(userId),
        this.getProfile(relatedUserId)
      ]);

      if (!userProfile || !relatedProfile) {
        throw new Error('One or both users not found');
      }

      if (userProfile.familyCode !== relatedProfile.familyCode) {
        throw new Error('Users must be in the same family to establish a relationship');
      }

      const reciprocalType = this.getReciprocalRelationship(relationshipType);
      if (!reciprocalType) {
        throw new Error('Invalid relationship type');
      }

      // Create relationship documents for both users
      const relationshipRef = doc(collection(db, 'users', userId, 'relationships'));
      const reciprocalRef = doc(collection(db, 'users', relatedUserId, 'relationships'));

      await Promise.all([
        setDoc(relationshipRef, {
          relatedUserId,
          relationshipType,
          createdAt: new Date(),
          updatedAt: new Date()
        }),
        setDoc(reciprocalRef, {
          relatedUserId: userId,
          relationshipType: reciprocalType,
          createdAt: new Date(),
          updatedAt: new Date()
        })
      ]);

      return true;
    } catch (error) {
      logger.error('Error adding relationship:', error);
      throw error;
    }
  }

  async getUserRelationships(userId) {
    try {
      const relationshipsRef = collection(db, 'users', userId, 'relationships');
      const relationshipsSnapshot = await getDocs(relationshipsRef);
      
      const relationships = [];
      for (const doc of relationshipsSnapshot.docs) {
        const data = doc.data();
        const relatedProfile = await this.getProfile(data.relatedUserId);
        relationships.push({
          id: doc.id,
          ...data,
          relatedProfile
        });
      }
      
      return relationships;
    } catch (error) {
      logger.error('Error getting user relationships:', error);
      throw error;
    }
  }

  async removeRelationship(userId, relatedUserId) {
    try {
      // Find and delete the relationship documents for both users
      const userRelationshipsRef = collection(db, 'users', userId, 'relationships');
      const relatedUserRelationshipsRef = collection(db, 'users', relatedUserId, 'relationships');

      const [userRelationships, relatedUserRelationships] = await Promise.all([
        getDocs(query(userRelationshipsRef, where('relatedUserId', '==', relatedUserId))),
        getDocs(query(relatedUserRelationshipsRef, where('relatedUserId', '==', userId)))
      ]);

      const deletePromises = [];

      userRelationships.forEach(doc => {
        deletePromises.push(deleteDoc(doc.ref));
      });

      relatedUserRelationships.forEach(doc => {
        deletePromises.push(deleteDoc(doc.ref));
      });

      await Promise.all(deletePromises);
      return true;
    } catch (error) {
      logger.error('Error removing relationship:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const relationshipService = new RelationshipService(); 